import { useEffect, useState } from 'react';
import { isNil } from 'es-toolkit';

import { canWriteHouseholdData } from '../constants/household-membership-role';
import { useFlagsAndLoadingState } from '../contexts/launch-darkly-flags-ready-context';
import { useHouseholdRole } from './use-household-role';

const ROLE_LABELS: Record<string, string> = {
  beneficiary: 'beneficiary',
  authorized_viewer: 'authorized viewer',
};

const ROLE_RESOLUTION_GRACE_MS = 8_000;

export type UseHouseholdWriteAccessResult = {
  readonly canWrite: boolean;
  readonly tooltip: string | null;
  readonly checking: boolean;
};

function useRoleResolutionStalled(loading: boolean): boolean {
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (!loading) {
      setStalled(false);
      return;
    }
    const timer = setTimeout(() => {
      setStalled(true);
    }, ROLE_RESOLUTION_GRACE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [loading]);

  return stalled;
}

export function useHouseholdWriteAccess(): UseHouseholdWriteAccessResult {
  const { flagsReady } = useFlagsAndLoadingState();
  const { role, loading } = useHouseholdRole();
  const stalled = useRoleResolutionStalled(flagsReady && loading);

  if (!flagsReady) {
    return { canWrite: true, tooltip: null, checking: false };
  }

  if (loading) {
    return {
      canWrite: false,
      checking: !stalled,
      tooltip: stalled
        ? "We couldn't verify your access to this household. Refresh, or contact your advisory team."
        : 'Checking your access to this household',
    };
  }

  if (isNil(role) || canWriteHouseholdData(role)) {
    return { canWrite: true, tooltip: null, checking: false };
  }

  const label = ROLE_LABELS[role] ?? role;
  return {
    canWrite: false,
    checking: false,
    tooltip: `Only owners and trustees can take this action. You're signed in as a ${label} on this household.`,
  };
}
